/** Current-URL hooks, read straight from TanStack. */
import { useMemo } from "react";
import { useRouter } from "@tanstack/react-router";

import { useCommittedLocation } from "./committedLocation";

export function useAppPathname(): string {
  return useCommittedLocation().pathname;
}

/**
 * The current path, tolerating a missing router — the error screen renders
 * where no router context exists at all, which `useRouterState` cannot
 * tolerate.
 *
 * `useRouter({ warn: false })` is the only hook that survives that: it hands
 * back `undefined` instead of throwing, and absence is the expected case here
 * rather than a mistake worth logging. The router state is read, not
 * subscribed to — the screen this feeds is terminal and leaves by a full page
 * load, so there is no navigation for it to follow.
 */
export function useAppPathnameOutsideProvider(): string {
  const router = useRouter({ warn: false }) as
    | ReturnType<typeof useRouter>
    | undefined;
  if (router) {
    return router.state.location.pathname;
  }
  return typeof window === "undefined" ? "" : window.location.pathname;
}

export function useAppSearchParams(): URLSearchParams {
  const { searchStr } = useCommittedLocation();
  return useMemo(() => new URLSearchParams(searchStr), [searchStr]);
}
